// The phone's building blocks, after the phone prototype: the top bar (back, or search, notifications with a badge
// and the avatar), the large title with the small line above it, grouped lists of compact rows (a title, a status
// line, a chevron), tags, the segmented control, the assistant's violet card, and the bottom sheet (on a spring,
// dragged down to close).
import { useRef, type ReactNode } from 'react';
import { ActLink } from '../ui/ActLink';
import { Link } from 'react-router-dom';
import { motion, useDragControls, useReducedMotion, type PanInfo } from 'motion/react';
import type { BriefKind } from '@dpw/shared';
import { useBrief, useSummary } from '../lib/queries';
import { scrim, spring } from '../lib/motion';
import { useSheetFocus } from '../ui/Sheet';
import { Skel } from '../ui/motion';
import { openPalette } from '../ui/CommandPalette';

const S = ({ w = 2, children }: { w?: number; children: ReactNode }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
export const P = {
  chev: () => <svg className="ph-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 5.5l6.5 6.5L9 18.5" /></svg>,
  back: () => <S w={2.3}><path d="M15 4.5L7.5 12l7.5 7.5" /></S>,
  check: () => <S w={2.8}><path d="M5 12.5l4.5 4.5L19 7.5" /></S>,
  x: () => <S><path d="M6 6l12 12M18 6L6 18" /></S>,
  mic: () => <S w={1.9}><rect x="9" y="3" width="6" height="11.5" rx="3" /><path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3" /></S>,
  send: () => <S w={2.2}><path d="M12 19V5M6 11l6-6 6 6" /></S>,
  plus: () => <S><path d="M12 5v14M5 12h14" /></S>,
  search: () => <S><circle cx="11" cy="11" r="6.5" /><path d="M20 20l-4.2-4.2" /></S>,
  bell: () => <S w={1.8}><path d="M6 9a6 6 0 0 1 12 0c0 6.5 2.5 8 2.5 8h-17S6 15.5 6 9M10 20.5a2 2 0 0 0 4 0" /></S>,
  clip: () => <S w={1.9}><path d="M20 11.5l-8.2 8.2a5 5 0 0 1-7.1-7.1l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7l-8.4 8.4a1.7 1.7 0 0 1-2.4-2.4l7.7-7.7" /></S>,
  wave: () => <S><path d="M4 10v4M8 7v10M12 4v16M16 7v10M20 10v4" /></S>,
  hist: () => <S w={1.9}><path d="M3.5 12a8.5 8.5 0 1 0 2.5-6M3.5 4.5V9H8" /><path d="M12 8v4.5l3 1.8" /></S>,
  compose: () => <S w={1.9}><path d="M12 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6" /><path d="M17.5 3.5l3 3L12 15l-4 1 1-4z" /></S>,
  spark: () => <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 3.5l1.9 5.1 5.1 1.9-5.1 1.9L12 17.5l-1.9-5.1L5 10.5l5.1-1.9z" /><path d="M18.5 15l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z" opacity=".8" /></svg>,
  clock: () => <S w={1.9}><path d="M12 7v5l3 2M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18" /></S>,
  folder: () => <S w={1.9}><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></S>,
  book: () => <S w={1.9}><path d="M5 4h11a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3zM5 17a3 3 0 0 1 3-3h11" /></S>,
  inbox: () => <S w={1.9}><path d="M4 13h4l2 3h4l2-3h4M5 5h14l1 8v6H4v-6z" /></S>,
  moon: () => <S w={1.9}><path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z" /></S>,
  gear: () => <S w={1.9}><circle cx="12" cy="12" r="3" /><path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M5.3 18.7l2.1-2.1M16.6 7.4l2.1-2.1" /></S>,
  list: () => <S w={1.9}><path d="M4 6h16M4 12h10M4 18h7" /></S>,
  people: () => <S w={1.9}><path d="M9 8a3 3 0 1 0 0-.01M3 20a6 6 0 0 1 12 0M17 9a2.5 2.5 0 1 0 0-.01M15.5 14.5A5 5 0 0 1 21 19" /></S>,
  doc: () => <S w={1.9}><path d="M7 3h7l5 5v13H7zM14 3v5h5" /></S>,
  home: () => <S w={1.8}><path d="M4 10.5L12 4l8 6.5V19a1.5 1.5 0 0 1-1.5 1.5H15v-6H9v6H5.5A1.5 1.5 0 0 1 4 19z" /></S>,
  cal: () => <S w={1.8}><rect x="3.5" y="5" width="17" height="15.5" rx="3" /><path d="M3.5 10h17M8 3v4M16 3v4" /></S>,
  grid: () => <S w={1.8}><rect x="3.5" y="3.5" width="7" height="7" rx="2" /><rect x="13.5" y="3.5" width="7" height="7" rx="2" /><rect x="3.5" y="13.5" width="7" height="7" rx="2" /><rect x="13.5" y="13.5" width="7" height="7" rx="2" /></S>,
  more: () => <S w={1.8}><circle cx="12" cy="12" r="8.5" /><circle cx="8" cy="12" r=".6" fill="currentColor" /><circle cx="12" cy="12" r=".6" fill="currentColor" /><circle cx="16" cy="12" r=".6" fill="currentColor" /></S>,
};

/** The top bar: a back link (or the lead, your avatar on Today) on the left, and the page's own buttons on the right. */
export function TopBar({ back, lead, children }: { back?: { to: string; label: string }; lead?: ReactNode; children?: ReactNode }) {
  return (
    <div className="ph-navb">
      {back && <Link className="ph-back" to={back.to} aria-label={`Back to ${back.label}`}><P.back /><span>{back.label}</span></Link>}
      {!back && lead}
      <span className="sp" />
      {children}
    </div>
  );
}
/** Search opens over the page, as a full-screen sheet on the phone. */
export const SearchButton = () => <button type="button" className="ph-nb" onClick={openPalette} aria-label="Search" aria-haspopup="dialog"><P.search /></button>;
export function BellButton() {
  const { data } = useSummary();
  const n = data?.notifications ?? 0;
  return <Link className="ph-nb" to="/notifications" aria-label={`Notifications, ${n} to dismiss`}><P.bell />{n > 0 && <span className="ph-cntb">{n}</span>}</Link>;
}
export const AvatarButton = ({ initials }: { initials: string }) => <Link className="ph-nb av" to="/settings" aria-label="Settings">{initials}</Link>;

/** The large title, with the small line above it (a date, a client, where it sits) and the line below it. */
export function Title({ eyebrow, title, sub, md, tags }: { eyebrow?: ReactNode; title: ReactNode; sub?: ReactNode; md?: boolean; tags?: ReactNode }) {
  return (
    <div className="ph-lt">
      {tags && <div className="tags">{tags}</div>}
      {eyebrow && <div className="eb">{eyebrow}</div>}
      <h1 className={md ? 'md' : undefined}>{title}</h1>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}
export function Section({ title, count, right }: { title: string; count?: number | string; right?: ReactNode }) {
  return <div className="ph-gh"><b>{title}{count !== undefined && <em>{count}</em>}</b>{right}</div>;
}

type RowBody = { icon?: ReactNode; iconTone?: string; kd?: ReactNode; title: ReactNode; sub?: ReactNode; right?: ReactNode; chevron?: boolean; ai?: boolean; className?: string; label?: string };
function RowInner({ icon, iconTone = '', kd, title, sub, right, chevron = true }: RowBody) {
  return (
    <>
      {icon && <span className={`ph-ic ${iconTone}`}>{icon}</span>}
      <span className="ph-rt">{kd && <div className="kd">{kd}</div>}<div className="t">{title}</div>{sub && <div className="s">{sub}</div>}</span>
      {right}
      {chevron && <P.chev />}
    </>
  );
}
/** A compact single-line row: an icon, the title, a status line, a chevron. A button (opens a sheet) or a link. */
export function Row(p: RowBody & ({ onClick: () => void; to?: undefined; activity?: undefined } | { to: string; onClick?: undefined; activity?: undefined } | { activity: string; to?: undefined; onClick?: undefined })) {
  const cls = `ph-row${p.ai ? ' ait' : ''}${p.className ? ` ${p.className}` : ''}`;
  // An activity opens in its sheet over the list (6.2).
  if (p.activity !== undefined) return <ActLink id={p.activity} className={cls} aria-label={p.label}><RowInner {...p} /></ActLink>;
  return p.to !== undefined
    ? <Link className={cls} to={p.to} aria-label={p.label}><RowInner {...p} /></Link>
    : <button type="button" className={cls} onClick={p.onClick} aria-label={p.label}><RowInner {...p} /></button>;
}

export function Tag({ tone = '', dot, children }: { tone?: '' | 'g' | 'a' | 'r' | 'ai'; dot?: boolean; children: ReactNode }) {
  return <span className={`ph-tag ${tone}`}>{dot && <i />}{children}</span>;
}

/** The segmented control (tabs). */
export function Seg<K extends string>({ value, options, onChange, label }: { value: K; options: readonly (readonly [K, ReactNode])[]; onChange: (k: K) => void; label: string }) {
  return (
    <div className="ph-seg" role="tablist" aria-label={label}>
      {options.map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={value === k} className={value === k ? 'on' : ''} onClick={() => onChange(k)}>{l}</button>)}
    </div>
  );
}

/** The assistant's card: violet edge, the orb and a label, then what it says. */
export function AiCard({ label, right, children, className = '' }: { label: string; right?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`ph-aic ${className}`} aria-label={label}>
      <div className="aih"><span className="aiorb" />{label}{right && <span className="up">{right}</span>}</div>
      {children}
    </section>
  );
}

/** A brief (a space's week, a track, the day) in the assistant's card: what it says, in a sentence or two. */
/** The paragraph when the brief has one (a space's week, a track, the day); else its lines. */
export function BriefCard({ kind, id, label }: { kind: BriefKind; id: string; label: string }) {
  const q = useBrief(kind, id);
  const b = q.data;
  return (
    <AiCard label={label} right={b?.stale ? 'Updating' : undefined}>
      {q.isLoading ? <div role="status" aria-label="Loading" style={{ display: 'grid', gap: 6 }}><Skel w="92%" h={12} /><Skel w="70%" h={12} /></div>
        : q.error || !b ? <p className="mu">The brief could not be loaded. Everything below is live.</p>
        : b.paragraph ? <p>{b.paragraph}</p>
        : b.lines.length ? b.lines.map(l => <p key={l.label}><b>{l.label}.</b> {l.text}</p>)
        : <p className="mu">Nothing to sum up yet. Everything below is live.</p>}
    </AiCard>
  );
}

/**
 * The bottom sheet: up on the gentle spring over a scrim; drag it down by its handle to close (or tap the scrim,
 * press Escape). Focus moves in and stays in while it is open. Render it inside <AnimatePresence>.
 */
export function PSheet({ label, onClose, children, card }: { label: string; onClose: () => void; children: ReactNode; /** The card it shows (for tests and keys). */ card?: { id: string; kind?: string | undefined } }) {
  const panel = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const drag = useDragControls();
  useSheetFocus(panel, onClose);
  const onDragEnd = (_: unknown, info: PanInfo) => { if (info.offset.y > 110 || info.velocity.y > 600) onClose(); };
  return (
    <>
      <motion.button type="button" className="ph-scrim" onClick={onClose} aria-label="Close" tabIndex={-1} variants={scrim} initial="initial" animate="animate" exit="exit" />
      <motion.div ref={panel} className="ph-bsh" role="dialog" aria-modal="true" aria-label={label} data-card={card?.id} data-kind={card?.kind}
        initial={reduce ? { opacity: 0 } : { y: '100%' }} animate={reduce ? { opacity: 1 } : { y: 0 }} exit={reduce ? { opacity: 0 } : { y: '100%' }}
        transition={spring.gentle}
        drag={reduce ? false : 'y'} dragControls={drag} dragListener={false} dragConstraints={{ top: 0, bottom: 0 }} dragElastic={{ top: 0.04, bottom: 0.85 }} onDragEnd={onDragEnd}>
        <div className="ph-grabz" data-grab onPointerDown={e => drag.start(e)} style={{ touchAction: 'none' }}><span className="ph-grab" aria-hidden="true" /></div>
        {children}
      </motion.div>
    </>
  );
}

/** A ring with a number in it (prep ready, how far along). */
export function Ring({ pct, color, children, big }: { pct: number; color: string; children: ReactNode; big?: boolean }) {
  return <span className={big ? 'ph-bigr' : 'ph-rg'} style={{ background: `conic-gradient(${color} ${pct}%, var(--s3) 0)` }}><span>{children}</span></span>;
}
